import { DrillCallout, DrillEquation, DrillRow, DrillSection, DrillText } from "@/components/dashboard/widgets/shared";
import type { MonthlyBillingData } from "@/lib/billing-rates-calc";
import type { EmployeeKpis, RoleDeviationSummary } from "@/lib/employee-kpis";
import { formatDate, formatHourlyRate, formatHours, formatNOK, formatPct, MONTH_NAMES_NB } from "@/lib/format";
import { billingStatus, deviationStatus } from "@/lib/widget-status";

/**
 * Innholdet i drillpanelet for hvert nøkkeltall på ansattkortet. Samme
 * oppbygning som Måltall: likningen med ekte tall først, så radene bak.
 */

export type EmployeeKpiDrill = "geek" | "rate" | "pl" | "seller" | "billing" | "internal" | "sales" | "sick";

const projectTitle = (code: string | null, name: string) => (code ? `${code} ${name}` : name);
const projectLink = (id: string) => `/prosjekter/${id}`;
const signedPct = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${formatPct(Math.abs(v))}`;

export function GeekShareDrill({ completed, year }: { completed: EmployeeKpis["completed"]; year: number }) {
  return (
    <>
      <DrillEquation
        terms={[
          { value: formatNOK(completed.actualGeekShare), label: "reell GEEK-andel" },
          "fra",
          { value: String(completed.projects.length), label: `prosjekter avsluttet i ${year}` },
        ]}
      />
      <DrillText>
        Per prosjekt: reell GEEK × egne timer / alle timer på prosjektet. Bare avsluttede, eksterne prosjekter
        der den ansatte har ført timer.
      </DrillText>
      {completed.projects.length > 0 ? (
        <DrillSection label="Prosjekter, største andel først">
          {completed.projects.map((p) => (
            <DrillRow
              key={p.projectId}
              title={projectTitle(p.projectCode, p.projectName)}
              value={formatNOK(p.geekShare)}
              sub={`${formatHours(p.employeeHours)} av ${formatHours(p.totalProjectHours)} · ferdig ${formatDate(p.completedAt)}`}
              tone={p.geekShare < 0 ? "miss" : undefined}
              to={projectLink(p.projectId)}
            />
          ))}
        </DrillSection>
      ) : (
        <DrillCallout tone="neutral">Ingen avsluttede prosjekter med egne timer i år.</DrillCallout>
      )}
    </>
  );
}

export function HourlyRateDrill({ completed }: { completed: EmployeeKpis["completed"] }) {
  const delta = completed.deltaVsOrganizationPct;
  const byRate = [...completed.projects].sort((a, b) => b.actualHourlyRate - a.actualHourlyRate);
  return (
    <>
      <DrillEquation
        terms={[
          { value: formatNOK(completed.actualGeekShare), label: "GEEK-andel" },
          "/",
          { value: formatHours(completed.employeeHours), label: "egne timer" },
          "=",
          { value: formatHourlyRate(completed.actualHourlyRate) },
        ]}
      />
      {completed.organizationHourlyRate != null && (
        <DrillText>
          Teamsnittet er {formatHourlyRate(completed.organizationHourlyRate)}, reell GEEK delt på alle timer på
          prosjekter avsluttet i år
          {delta != null && <>. Den ansatte ligger {formatPct(Math.abs(delta))} {delta >= 0 ? "over" : "under"}</>}.
        </DrillText>
      )}
      {byRate.length > 0 ? (
        <DrillSection label="Reell timepris per prosjekt">
          {byRate.map((p) => (
            <DrillRow
              key={p.projectId}
              title={projectTitle(p.projectCode, p.projectName)}
              value={formatHourlyRate(p.actualHourlyRate)}
              sub={`${formatHours(p.employeeHours)} egne timer`}
              tone={
                completed.organizationHourlyRate == null ? undefined
                  : p.actualHourlyRate >= completed.organizationHourlyRate ? "reached" : "miss"
              }
              to={projectLink(p.projectId)}
            />
          ))}
        </DrillSection>
      ) : (
        <DrillCallout tone="neutral">Ingen avsluttede prosjekter med egne timer i år.</DrillCallout>
      )}
    </>
  );
}

export function RoleDeviationDrill({ summary, role }: { summary: RoleDeviationSummary; role: "pl" | "seller" }) {
  return (
    <>
      <DrillEquation
        terms={[
          { value: formatHours(summary.loggedHours), label: "brukt" },
          "mot",
          { value: formatHours(summary.soldHours), label: "solgt" },
          "=",
          summary.deviationPct == null
            ? { value: "—" }
            : { value: signedPct(summary.deviationPct), tone: deviationStatus(summary.deviationPct).tone },
        ]}
      />
      <DrillText>
        {role === "pl"
          ? "Prosjekter avsluttet i år der den ansatte er prosjektleder. Alle timer på prosjektet mot solgte timer i budsjettet."
          : "Prosjekter avsluttet i år der den ansatte er salgseier. Timene vektes med eierandelen før de summeres."}
      </DrillText>
      {summary.projects.length > 0 ? (
        <DrillSection label="Prosjekter, størst overskridelse først">
          {summary.projects.map((p) => (
            <DrillRow
              key={p.projectId}
              title={projectTitle(p.projectCode, p.projectName)}
              value={p.deviationPct == null ? "—" : signedPct(p.deviationPct)}
              sub={`${formatHours(p.loggedHours)} brukt / ${formatHours(p.soldHours)} solgt${
                role === "seller" ? ` · eierandel ${formatPct(p.weight * 100, 0)}` : ""
              }`}
              tone={p.deviationPct == null ? undefined : deviationStatus(p.deviationPct).tone}
              to={projectLink(p.projectId)}
            />
          ))}
        </DrillSection>
      ) : (
        <DrillCallout tone="neutral">Ingen avsluttede prosjekter i rollen i år.</DrillCallout>
      )}
    </>
  );
}

export function BillingRateDrill({
  ytdRate, target, months, currentMonth, year,
}: {
  ytdRate: number | null;
  target: number | null;
  months: MonthlyBillingData[];
  currentMonth: number;
  year: number;
}) {
  const shown = months.filter((m) => m.month <= currentMonth && m.capacity > 0);
  return (
    <>
      <DrillEquation
        terms={[
          {
            value: ytdRate == null ? "—" : formatPct(ytdRate),
            label: `hittil i ${year}`,
            tone: ytdRate != null && target != null ? billingStatus(ytdRate, target).tone : undefined,
          },
          "mot",
          { value: target == null ? "—" : formatPct(target, 0), label: "mål" },
        ]}
      />
      <DrillText>
        Fakturerbare timer delt på tilgjengelige timer (kapasitet minus fravær og helligdager). Hittil i år teller til og med
        siste avstemte måned. Grunnlaget per oppgave ligger under Mål.
      </DrillText>
      <DrillSection label={`Per måned ${year}`}>
        {shown.map((m) => (
          <DrillRow
            key={m.month}
            title={MONTH_NAMES_NB[m.month - 1]}
            value={m.rate == null ? "—" : formatPct(m.rate)}
            sub={`${formatHours(m.billableHours)} fakturerbare / ${formatHours(m.capacity)} tilgjengelige${
              m.confirmed ? "" : " · ikke lukket"
            }`}
            tone={m.rate != null && target != null ? billingStatus(m.rate, target).tone : undefined}
          />
        ))}
      </DrillSection>
    </>
  );
}

export function InternalTimeDrill({ internalTime, year }: { internalTime: EmployeeKpis["internalTime"]; year: number }) {
  return (
    <>
      <DrillEquation
        terms={[
          { value: formatHours(internalTime.hours), label: "interne timer" },
          "/",
          { value: formatHours(internalTime.totalLoggedHours), label: `loggført i ${year}` },
          "=",
          { value: internalTime.sharePct == null ? "—" : formatPct(internalTime.sharePct) },
        ]}
      />
      {internalTime.projects.length > 0 ? (
        <DrillSection label="Interne prosjekter">
          {internalTime.projects.map((p) => (
            <DrillRow
              key={p.projectId}
              title={projectTitle(p.projectCode, p.projectName)}
              value={formatHours(p.hours)}
              to={projectLink(p.projectId)}
            />
          ))}
        </DrillSection>
      ) : (
        <DrillCallout tone="neutral">Ingen timer på interne prosjekter i år.</DrillCallout>
      )}
    </>
  );
}

export function SalesDrill({ sales, year }: { sales: EmployeeKpis["sales"]; year: number }) {
  return (
    <>
      <DrillEquation
        terms={[
          { value: formatNOK(sales.attributedGeek), label: "GEEK etter eierandel" },
          "fra",
          { value: String(sales.projectCount), label: `salg signert i ${year}` },
        ]}
      />
      <DrillText>
        GEEK × eierandel. Reell GEEK når prosjektet er avsluttet, ellers estimert. Kansellerte salg teller ikke.
      </DrillText>
      {sales.projects.length > 0 ? (
        <DrillSection label="Salg, størst andel først">
          {sales.projects.map((p) => (
            <DrillRow
              key={p.projectId}
              title={projectTitle(p.projectCode, p.projectName)}
              value={formatNOK(p.attributedGeek)}
              sub={`${formatNOK(p.geek)} ${p.isActual ? "reell" : "estimert"} × ${formatPct(p.ownerSharePct, 0)} · signert ${formatDate(p.closedAt)}`}
              tone={p.attributedGeek < 0 ? "miss" : undefined}
              to={projectLink(p.projectId)}
            />
          ))}
        </DrillSection>
      ) : (
        <DrillCallout tone="neutral">Ingen signerte salg i år.</DrillCallout>
      )}
    </>
  );
}

export function SickLeaveDrill({ sickLeave, year }: { sickLeave: EmployeeKpis["sickLeave"]; year: number }) {
  return (
    <>
      <DrillEquation
        terms={[
          { value: formatHours(sickLeave.hours), label: `sykefravær i ${year}` },
          "=",
          {
            value: sickLeave.days.toLocaleString("nb-NO", { maximumFractionDigits: 1 }),
            label: "arbeidsdager à 7,5 t",
          },
        ]}
      />
      <DrillText>Godkjente og ventende sykemeldinger. Avviste teller ikke.</DrillText>
      {sickLeave.entries.length > 0 ? (
        <DrillSection label="Registreringer">
          {sickLeave.entries.map((e) => (
            <DrillRow
              key={`${e.startDate}-${e.endDate}`}
              title={e.startDate === e.endDate ? formatDate(e.startDate) : `${formatDate(e.startDate)} – ${formatDate(e.endDate)}`}
              value={formatHours(e.hours)}
              sub={e.status === "pending" ? "Venter på godkjenning" : "Godkjent"}
              tone={e.status === "pending" ? "near" : undefined}
            />
          ))}
        </DrillSection>
      ) : (
        <DrillCallout tone="neutral">Ingen sykefravær registrert i år.</DrillCallout>
      )}
    </>
  );
}
